import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { AgentSummary } from '../../types/agent.js';
import { AgentStateController } from '../../services/agent-state.js';
import { getAvatarUrl } from '../../services/agent-api.js';

@customElement('cp-agent-selector')
export class CpAgentSelector extends LitElement {
  createRenderRoot() { return this; }

  private _agentState = new AgentStateController(this);

  @property({ type: Number }) value: number | null = null;
  @property({ type: Boolean }) disabled = false;

  @state() private _open = false;

  connectedCallback(): void {
    super.connectedCallback();
    void this._agentState.loadAll();
    document.addEventListener('click', this._onDocumentClick);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this._onDocumentClick);
  }

  private _onDocumentClick = (e: MouseEvent): void => {
    if (this._open && !this.contains(e.target as Node)) {
      this._open = false;
    }
  };

  private _toggle(): void {
    if (this.disabled) return;
    this._open = !this._open;
  }

  private _selectAgent(agent: AgentSummary): void {
    this.value = agent.id;
    this._open = false;
    this.dispatchEvent(new CustomEvent('agent-selected', {
      detail: { agentId: agent.id, agent },
      bubbles: true,
      composed: true,
    }));
  }

  private _getSelectedAgent(): AgentSummary | undefined {
    return this._agentState.agents.find(a => a.id === this.value);
  }

  private _renderAvatar(agent: AgentSummary) {
    if (agent.has_avatar) {
      return html`<img class="cp-agent-selector__avatar" src="${getAvatarUrl(agent.id)}" alt="${agent.name}" />`;
    }
    return html`
      <div class="cp-agent-selector__avatar cp-agent-selector__avatar--initials" style="background-color: ${agent.color || 'var(--color-bg-tertiary)'}">
        ${agent.name.charAt(0).toUpperCase()}
      </div>
    `;
  }

  render() {
    const selected = this._getSelectedAgent();

    return html`
      <div class="cp-agent-selector ${this._open ? 'cp-agent-selector--open' : ''} ${this.disabled ? 'cp-agent-selector--disabled' : ''}">
        <button
          class="cp-agent-selector__trigger"
          @click=${() => this._toggle()}
          ?disabled=${this.disabled}
          type="button"
        >
          ${selected ? html`
            <div class="cp-agent-selector__selected">
              ${this._renderAvatar(selected)}
              <span class="cp-agent-selector__name">
                ${selected.name}${selected.is_main ? ' (Haupt)' : ''}
              </span>
            </div>
          ` : html`
            <span class="cp-agent-selector__placeholder">Agent waehlen...</span>
          `}
          <span class="cp-agent-selector__chevron">&#9662;</span>
        </button>

        ${this._open ? html`
          <div class="cp-agent-selector__dropdown">
            ${this._agentState.agents.length === 0 ? html`
              <div class="cp-agent-selector__empty">Keine Agenten vorhanden</div>
            ` : nothing}
            ${this._agentState.agents.map(agent => html`
              <button
                class="cp-agent-selector__option ${this.value === agent.id ? 'cp-agent-selector__option--selected' : ''}"
                @click=${() => this._selectAgent(agent)}
                type="button"
              >
                ${this._renderAvatar(agent)}
                <div class="cp-agent-selector__option-info">
                  <span class="cp-agent-selector__option-name">${agent.name}</span>
                  <span class="cp-agent-selector__option-meta">
                    ${agent.is_main ? '(Haupt)' : agent.personality_type || 'Agent'}
                  </span>
                </div>
              </button>
            `)}
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-agent-selector': CpAgentSelector;
  }
}
